import dynamic from 'next/dynamic'
import Link from 'next/link'
import Router from 'next/router'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'
import clsx from 'clsx'
import arrayMutators from 'final-form-arrays'
import escape from 'lodash-es/escape'

import { SubnetType } from '@infura/key-service-sdk/lib/v1/proto/service_plan/v1/networks'
import { CustomerOnboardingInput, User } from '@infura/packages.api'
import { Analytics } from '@infura/packages.components/Analytics'
import { Button } from '@infura/packages.ui/Button'
import { ServiceTier, V1Project } from '@infura/packages.uks'

import { MetaMaskLogo } from 'components/MetaMaskLogo'
import { ISurveyQuestionData, ISurveyQuestions, OnboardingFormState } from 'components/onboardingWelcome'
import { SurveyProgress, getOnboardingSurveyQuestionsSchema, useSurvey } from 'components/onboardingWelcome'

import { api } from 'lib/api/api'
import { uks, useUks } from 'lib/api/uks'
import handleSubmissionError from 'lib/handleSubmissionError'

const DEFAULT_COMPONENT = 'Question'

export const WELCOME_SESSION = {
  name: 'welcome_onboarding',
  ongoing: 'ongoing',
  skip: 'skipped'
}

const WelcomePage = ({
  isGrowthPlanDisabled,
  isOnCreditPlan,
  networksConnected,
  questionsList,
  user,
  userKeys
}: {
  networksConnected: number
  questionsList: Array<ISurveyQuestionData>
  user: User
  userKeys: { [key: string]: V1Project }
  isOnCreditPlan: boolean
  isGrowthPlanDisabled: boolean
}) => {
  const { data: networksData } = useUks(['networkMethodsList', user?.id], {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
    shouldRetryOnError: true
  })
  const [initialQuestionsList] = useState(questionsList)
  const { currentQuestion, currentStep, handleNext, handlePrevious, isLastPage, steps, validate } = useSurvey({
    questions: initialQuestionsList
  })
  const [isLastPageState, setIsLastPageState] = useState(isLastPage)
  const [stepsState, setStepsState] = useState(steps)
  const [allNetworksConnected, setAllNetworksConnected] = useState(false)

  const networks = useMemo(() => {
    if (networksData?.result) {
      return networksData.result
    }
  }, [networksData])

  const handleSubmitUserData = async values => {
    localStorage.setItem(WELCOME_SESSION.name, WELCOME_SESSION.ongoing)

    if (networksConnected === 0 && !allNetworksConnected) {
      const userKeysLength = Object.values(userKeys).length
      let apiKeyId = ''

      if (userKeysLength === 0) {
        const { data } = await uks.projectMethodsUpsert({
          name: 'My First Key',
          settings: { basic: { description: '' } },
          userId: user.id
        })
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        await uks.securityMethodsGenerate(data.result.id, {})
        // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
        apiKeyId = data.result.id
      }

      if (userKeysLength >= 1) {
        // @ts-ignore
        apiKeyId = Object.values(userKeys)[0].id
      }

      if (networks) {
        for (const [networkName, networkData] of Object.entries(networks)) {
          if (!networkData.disabled) {
            // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
            const subnetsToAdd = Object.keys(networkData.subnets)
              // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
              .filter(subnetId => !networkData.subnets[subnetId].disabled)
              .map(subnetId => SubnetType[subnetId])
            await uks.projectMethodsUpdateSubnets(apiKeyId, subnetsToAdd, networkName)
          }
        }
        setAllNetworksConnected(true)
      }
    }

    if (!user?.customer_onboarded) {
      const categoryOtherClean = Object.keys(values).find(key => key.includes('category_other'))
      const roleOtherClean = Object.keys(values).find(key => key.includes('role_other'))

      await api.updateCustomerOnboarding(user.id, {
        ...values,
        ...(categoryOtherClean && { category_other: escape(values[categoryOtherClean]) }),
        ...(roleOtherClean && { role_other: escape(values[roleOtherClean]) }),
        is_fully_onboarded: true
      })
    } else {
      await api.updateCustomerOnboarding(user.id, {
        ...user.customer_onboarding,
        is_fully_onboarded: true
      } as CustomerOnboardingInput)
    }
  }

  const handleFinishOnboarding = () => {
    localStorage.setItem(WELCOME_SESSION.name, WELCOME_SESSION.skip)
    Router.replace('/', undefined, { shallow: true })
  }

  const handleFormSubmission = async (values: OnboardingFormState) => {
    try {
      if (!isLastPageState) {
        if (currentQuestion.id === 'select_your_category') {
          await handleSubmitUserData(values)
        }

        return handleNext()
      }
      await handleSubmitUserData(values)
    } catch (e: any) {
      return handleSubmissionError(e)
    }

    handleFinishOnboarding()
  }

  const handleFormsStateUpdate = ({ values }: { values: OnboardingFormState }) => {
    if (currentQuestion.id === 'plan_selection' && values.tier === ServiceTier.CORE) {
      setIsLastPageState(true)
    } else {
      setIsLastPageState(isLastPage)
    }

    if (values.tier === ServiceTier.CORE) {
      setStepsState(steps - 1)
    } else {
      setStepsState(steps)
    }
  }

  const isSkipDisabled = !user?.customer_onboarded && networksConnected === 0

  // Load dynamically our Form steps components
  const SurveyComponent: React.ComponentType<ISurveyQuestions> = dynamic(
    () => import(`./${currentQuestion.component ?? DEFAULT_COMPONENT}`)
  )

  const renderTotalPrice = (values: OnboardingFormState, currentQuestion: ISurveyQuestionData) => {
    if (!values.tier) return <></>

    const numberWithCommas = x => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    const tierPrice = currentQuestion.datas[0].options
      .find(option => option.id === values.tier)
      .subtitle.replace('US$', '')
    const addonsPrice = values.addonSelected && values.addonQuantity * 200
    const totalPrice = Number.isNaN(Number(tierPrice)) ? (
      <span>{tierPrice}</span>
    ) : (
      <span>
        {/* @ts-expect-error enabled to support strictNullChecks - Please fix this if you can! */}
        US${numberWithCommas(+tierPrice + addonsPrice)}
        {'.00 '}
        {<span className='text-text-alternative text-base !font-normal'>/month</span>}
      </span>
    )

    return (
      <div className='-mt-1.5 mb-2 flex w-full justify-between md:-mb-1.5 md:inline-flex md:w-auto md:flex-col'>
        <span>Subtotal:</span>
        <span className='text-primary-default text-2xl font-bold leading-none'>{totalPrice}</span>
      </div>
    )
  }

  const mutators = useMemo(
    () => ({
      addonPlanEnabled: (args, state, utils) => {
        if (args[0]) {
          utils.changeValue(state, 'addonSelected', () => true)
          utils.changeValue(state, 'addonQuantity', () => 1)
        } else {
          utils.changeValue(state, 'addonSelected', () => false)
        }
      },
      setApiKey: (args, state, utils) => {
        utils.changeValue(state, 'api_key', () => args[0])
      },
      setTextToEmpty: (args, state, utils) => {
        utils.changeValue(state, args[0], () => '')
      },
      setTier: (args, state, utils) => {
        utils.changeValue(state, 'tier', () => args[0])
      },
      ...arrayMutators
    }),
    []
  )

  useEffect(() => {
    setIsLastPageState(isLastPage)
    setStepsState(steps)
  }, [isLastPage, steps])

  return (
    <div className='w-full text-white'>
      <Form<OnboardingFormState>
        initialValues={{}}
        mutators={mutators}
        onSubmit={handleFormSubmission}
        render={({ handleSubmit, submitting, values }) => (
          <form onSubmit={handleSubmit}>
            <div className='flex min-h-screen flex-col'>
              <div
                className={`${stepsState > 1 ? 'mt-44' : 'mt-24'} mx-auto mb-44 w-full ${
                  currentQuestion.component === 'PlanTier' ? 'max-w-[1280px]' : 'max-w-[772px]'
                } p-5 md:mb-28 md:mt-0`}
              >
                <div className='bg-background-default fixed left-0 top-0 z-30 w-full pb-5 pt-8 text-center backdrop-blur-md md:relative md:z-10 md:bg-transparent md:backdrop-blur-none'>
                  <Link href='/' legacyBehavior passHref>
                    <a className={`${stepsState > 1 ? 'mb-8' : 'mb-0'} inline-flex flex-row items-center`}>
                      <MetaMaskLogo />
                    </a>
                  </Link>
                  {stepsState > 1 ? (
                    <SurveyProgress
                      currentStep={currentStep}
                      data={currentQuestion['sideBarData']}
                      steps={stepsState}
                    />
                  ) : null}
                </div>
                <div
                  className={clsx(
                    'pb-5',
                    (currentQuestion.id !== 'payment' ||
                      (currentQuestion.id === 'payment' && values.tier === ServiceTier.ENTERPRISE)) &&
                      'bg-background-default rounded-md border drop-shadow-md',
                    currentQuestion.id === 'payment' && values.tier === ServiceTier.ENTERPRISE && 'm-auto max-w-[530px]'
                  )}
                >
                  {currentQuestion?.datas?.map(data => (
                    <Fragment key={data.id}>
                      <SurveyComponent
                        {...data}
                        handleFinishOnboarding={handleFinishOnboarding}
                        handleNext={handleNext}
                        isGrowthPlanDisabled={isGrowthPlanDisabled}
                        isOnCreditPlan={isOnCreditPlan}
                        userKeys={userKeys}
                      />
                    </Fragment>
                  ))}
                </div>
              </div>

              <div className='fixed bottom-0 z-10 mt-auto w-full border-t border-gray-600 p-5 backdrop-blur-md md:p-4'>
                <div
                  className={`mx-auto flex max-w-5xl flex-wrap ${currentStep > 0 ? 'justify-between' : 'justify-end'}`}
                >
                  {currentStep > 0 ? (
                    <div className='order-2 mb-4 w-full md:order-1 md:mb-0 md:w-auto'>
                      <Button
                        className='w-full md:w-auto'
                        disabled={submitting}
                        onClick={handlePrevious}
                        theme='secondary'
                        type='button'
                      >
                        Previous
                      </Button>
                    </div>
                  ) : null}
                  {currentQuestion.id !== 'payment' ? (
                    <div className='order-1 inline-flex w-full flex-wrap align-top md:order-2 md:w-auto'>
                      <span>
                        {currentQuestion.id === 'plan_selection'
                          ? renderTotalPrice(values, currentQuestion)
                          : !isSkipDisabled &&
                            !submitting && (
                              <Analytics
                                action='click'
                                event={{
                                  location: 'Onboarding Survey Do It Later',
                                  text: 'Skip',
                                  type: 'Button'
                                }}
                                onClick={handleFinishOnboarding}
                              >
                                <Button
                                  className='hidden last:mb-0 md:inline-block'
                                  disabled={submitting}
                                  theme='secondary'
                                  type='button'
                                >
                                  Skip
                                </Button>
                              </Analytics>
                            )}
                      </span>
                      <Analytics
                        action='click'
                        event={{
                          location: 'Onboarding Survey CTA',
                          text: currentQuestion['nextCTA'],
                          type: 'Button'
                        }}
                      >
                        <Button
                          className='order-1 mb-4 h-max w-full md:mb-0 md:ml-4 md:w-auto'
                          data-testid='survey-page-next'
                          type='submit'
                        >
                          {submitting ? (
                            <span>
                              <FontAwesomeIcon className='fa-fw fa-spin mr-1' icon={faSpinnerThird} />
                            </span>
                          ) : null}
                          <span>{isLastPageState ? 'Start building' : currentQuestion['nextCTA']}</span>
                        </Button>
                      </Analytics>
                    </div>
                  ) : null}
                  {/* Responsive view order */}
                  {currentQuestion.id !== 'payment' &&
                  currentQuestion.id !== 'plan_selection' &&
                  !isSkipDisabled &&
                  !submitting ? (
                    <Analytics
                      action='click'
                      event={{
                        location: 'Onboarding Survey Do It Later',
                        text: 'Skip',
                        type: 'Button'
                      }}
                      onClick={handleFinishOnboarding}
                    >
                      <Button
                        className='order-last w-full  last:mb-0 md:hidden'
                        disabled={submitting}
                        theme='secondary'
                        type='button'
                      >
                        Skip
                      </Button>
                    </Analytics>
                  ) : null}
                </div>
              </div>
            </div>
            <FormSpy<OnboardingFormState>
              onChange={state => {
                handleFormsStateUpdate(state)
              }}
              subscription={{ valid: true, values: true }}
            />
          </form>
        )}
        validate={values => validate(currentStep, values, getOnboardingSurveyQuestionsSchema(initialQuestionsList))}
      />
    </div>
  )
}

export default WelcomePage
